import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const BOXHERO_LOGO_COLOR =
  "https://customer-assets.emergentagent.com/job_client-hub-266/artifacts/ysfg7n2y_Asset%209blue.png";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setInvite(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (password.length < 6) { setSubmitErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setSubmitErr("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post(`/invites/${token}/accept`, { password });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setSubmitErr(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredCard><p className="text-slate-500 text-sm">Validating invitation…</p></CenteredCard>;
  }

  if (error) {
    return (
      <CenteredCard testId="invite-error">
        <div className="w-14 h-14 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h1 className="text-xl font-bold text-bh-navy-deep mt-4" style={{ fontFamily: "Manrope" }}>
          This invitation link isn't valid
        </h1>
        <p className="text-bh-body mt-3 leading-relaxed">{error}</p>
        <div className="mt-6 bg-bh-bg border border-bh-divider rounded-lg p-4 text-sm text-left">
          <p className="font-semibold text-bh-navy-deep mb-2">A couple of common reasons:</p>
          <ul className="space-y-1.5 text-bh-body list-disc list-inside">
            <li>You may have clicked an older invitation that was replaced by a newer one.</li>
            <li>The link has been used to set a password already — try <Link to="/login" className="text-bh-blue hover:underline font-medium">signing in</Link>.</li>
            <li>The invitation may have expired (links are valid for 7 days).</li>
          </ul>
        </div>
        <p className="text-sm text-bh-muted mt-6">
          Email <a data-testid="invite-support-email" href="mailto:3pl@boxhero.com" className="text-bh-blue hover:underline font-medium">3pl@boxhero.com</a> to request a fresh invitation, or{" "}
          <Link to="/login" className="text-bh-blue font-medium hover:underline">sign in</Link>{" "}
          if you already have an account.
        </p>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard testId="invite-accept-page">
      <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-6 h-6" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mt-4" style={{ fontFamily: "Manrope" }}>
        Welcome to BoxHero, {(invite.name || "").split(" ")[0]}!
      </h1>
      <p className="text-slate-600 mt-3 text-center">
        You've been invited to the dashboard for <strong>{invite.company_name}</strong> by {invite.invited_by}. Set a password below to finish setting up.
      </p>

      <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg p-3 text-left text-sm">
        <div className="flex items-center justify-between"><span className="text-slate-500">Name</span><span className="font-medium text-slate-900">{invite.name}</span></div>
        <div className="flex items-center justify-between mt-1"><span className="text-slate-500">Email</span><span className="font-medium text-slate-900">{invite.email}</span></div>
        <div className="flex items-center justify-between mt-1"><span className="text-slate-500">Company</span><span className="font-medium text-slate-900">{invite.company_name}</span></div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="invite-form">
        <PasswordField label="Choose a password" testId="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
        <PasswordField label="Confirm password" testId="invite-password-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />

        {submitErr && (
          <div data-testid="invite-submit-error" className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">{submitErr}</div>
        )}

        <button
          type="submit"
          data-testid="invite-submit-btn"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors"
        >
          {submitting ? "Setting up your account…" : "Accept invitation & sign in"}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children, testId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div data-testid={testId} className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-10 shadow-sm text-center">
        <div className="flex items-center justify-center mb-6">
          <img src={BOXHERO_LOGO_COLOR} alt="BoxHero" className="h-10 w-auto" />
        </div>
        {children}
      </div>
    </div>
  );
}

function PasswordField({ label, testId, ...props }) {
  return (
    <div className="text-left">
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <input
        type="password"
        autoComplete="new-password"
        required
        data-testid={testId}
        {...props}
        className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-slate-900"
      />
    </div>
  );
}
